import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Grid,
} from '@mui/material';
import { LinkedOrganization } from 'models/user';
import { Organization } from 'models/organization';

type ImpersonateUserOrganizationsDialogProps = {
    handleClose: () => void;
    open: boolean;
    linkedOrganizations: LinkedOrganization[];
    handleConfirm: (organizationId: string, wlLabId: string | undefined) => void;
};

const ImpersonateUserOrganizationsDialog: FC<ImpersonateUserOrganizationsDialogProps> = ({
    handleClose,
    open,
    linkedOrganizations,
    handleConfirm,
}: ImpersonateUserOrganizationsDialogProps) => {
    const { t: translate } = useTranslation(['common']);
    const [selectedOrganization, setSelectedOrganization] = useState({} as Organization);
    const [wlLabId, setWlLabId] = useState<string | undefined>();
    const [showLabsDropDown, setShowLabsDropDown] = useState(false);
    // A white-label organization is entered through one of its labs. Super admins may
    // pick any connected lab, disabled ones included.
    const selectOrganization = (organization?: Organization) => {
        if (!organization) {
            return;
        }
        setSelectedOrganization(organization);
        setShowLabsDropDown(!!organization.isWhiteLabeled);
        setWlLabId(organization.isWhiteLabeled ? organization.connectedLabs?.[0]?.lab?._id : undefined);
    };

    useEffect(() => {
        if (open) {
            selectOrganization(linkedOrganizations?.[0]?.organization as Organization);
        }
    }, [open]);

    const onOrganizationChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        selectOrganization(
            linkedOrganizations?.find((linkedOrganization) => {
                return (linkedOrganization.organization as Organization)._id === value;
            })?.organization as Organization,
        );
    };

    const onWlLabChange = (event: SelectChangeEvent) => {
        setWlLabId(event.target.value);
    };

    const close = () => {
        setSelectedOrganization({} as Organization);
        setWlLabId(undefined);
        setShowLabsDropDown(false);
        handleClose();
    };

    const connectedLabs = selectedOrganization?.connectedLabs;

    return (
        <div>
            <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
                <DialogTitle id="alert-dialog-title">{translate('common:ORGANIZATION')}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item md={12} sm={12}>
                            <FormControl margin="dense" fullWidth>
                                <InputLabel required>{translate('common:ORGANIZATION')}</InputLabel>
                                <Select
                                    value={selectedOrganization?._id || ''}
                                    label={translate('common:ORGANIZATION')}
                                    onChange={onOrganizationChange}
                                >
                                    {linkedOrganizations?.map((linkedOrganization) => {
                                        const organization = linkedOrganization?.organization as Organization;

                                        return (
                                            <MenuItem key={organization?._id} value={organization?._id}>
                                                {organization?.name}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        {showLabsDropDown && (
                            <Grid item md={12} sm={12}>
                                <FormControl margin="dense" fullWidth>
                                    <InputLabel required>{translate('common:WHITE_LABEL_LABS')}</InputLabel>
                                    <Select
                                        value={wlLabId ?? ''}
                                        label={translate('common:WHITE_LABEL_LABS')}
                                        onChange={onWlLabChange}
                                        disabled={!connectedLabs?.length}
                                    >
                                        {connectedLabs?.length > 0 ? (
                                            connectedLabs.map((connectedLab) => (
                                                <MenuItem key={connectedLab?.lab?._id} value={connectedLab?.lab?._id}>
                                                    {connectedLab?.lab?.name}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem disabled>{translate('common:NO_LABS_AVAILABLE')}</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>{translate('common:CANCEL')}</Button>
                    <Button
                        disabled={!selectedOrganization._id || (showLabsDropDown && !wlLabId)}
                        onClick={() => handleConfirm(selectedOrganization._id, wlLabId)}
                        variant="contained"
                        autoFocus
                    >
                        {translate('common:CONFIRM')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ImpersonateUserOrganizationsDialog;
